import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /** Cart Liquid opts in via `data-can-remove`; `quantity_rule.min > 1` stays disabled at min. */
  #canRemoveOnDecrement() {
    const { min } = this.getCurrentValues();
    return this.dataset.canRemove === 'true' && min <= 1;
  }

  updateButtonStates() {
    super.updateButtonStates();

    const { minusButton, minusLabel } = this.refs;
    const { min, value } = this.getCurrentValues();
    const atMinimum = value <= min;
    const canRemoveOnDecrement = this.#canRemoveOnDecrement();

    if (atMinimum && canRemoveOnDecrement && !this.serverDisabledMinus) {
      minusButton.disabled = false;
    }

    if (!(minusLabel instanceof HTMLElement)) return;

    const removeLabel = this.dataset.removeLabel;
    const decreaseLabel = this.dataset.decreaseLabel;

    if (atMinimum && canRemoveOnDecrement && removeLabel) {
      minusLabel.textContent = removeLabel;
    } else if (decreaseLabel) {
      minusLabel.textContent = decreaseLabel;
    }
  }

  /**
   * At min with remove allowed, emit quantity 0 so cart-items can run `onLineItemRemove`
   * without flashing 0 in the input.
   * @param {Event} event
   */
  decreaseQuantity(event) {
    const { min, value } = this.getCurrentValues();

    if (value <= min && this.#canRemoveOnDecrement()) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.dispatchEvent(
        new QuantitySelectorUpdateEvent(0, Number(this.refs.quantityInput.dataset.cartLine) || undefined)
      );
      return;
    }

    super.decreaseQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
